import type { InterviewQuestion } from "@scoach/types";
import { useMemo, useState } from "react";

export interface QuestionBrowserProps {
  questions: InterviewQuestion[];
  selectedIds: Set<string>;
  recommendedIds: Set<string>;
  onToggle: (q: InterviewQuestion) => void;
  /** Bulk select/unselect a set of questions (the currently-filtered list). */
  onBulk?: (questions: InterviewQuestion[], select: boolean) => void;
}

/**
 * Browse the full question bank for an interview type: searchable, grouped by
 * skill, with checkboxes. Level-recommended questions are pre-checked (in the
 * parent) and badged here.
 */
export function QuestionBrowser({
  questions,
  selectedIds,
  recommendedIds,
  onToggle,
  onBulk,
}: QuestionBrowserProps) {
  const [q, setQ] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("");

  const levels = useMemo(() => {
    const s = new Set<string>();
    for (const it of questions) for (const l of it.levels ?? []) s.add(l);
    return [...s].sort();
  }, [questions]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return questions.filter((it) => {
      if (needle && !it.text.toLowerCase().includes(needle)) return false;
      if (levelFilter && !(it.levels ?? []).includes(levelFilter)) return false;
      return true;
    });
  }, [questions, q, levelFilter]);

  // Group by first skill (a question can measure several; the chips show all).
  const groups = useMemo(() => {
    const m = new Map<string, InterviewQuestion[]>();
    for (const it of filtered) {
      const key = it.skills?.[0] ?? "General";
      const arr = m.get(key) ?? [];
      arr.push(it);
      m.set(key, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input
          style={{
            flex: 1,
            minWidth: 200,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid var(--border-1)",
            background: "var(--surface-2)",
            color: "var(--text-1)",
            fontSize: 14,
          }}
          placeholder="Search questions…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          style={{
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid var(--border-1)",
            background: "var(--surface-2)",
            color: "var(--text-1)",
            fontSize: 14,
          }}
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
        >
          <option value="">All levels</option>
          {levels.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        {onBulk && (
          <>
            <button
              type="button"
              className="pill-btn"
              onClick={() => onBulk(filtered, true)}
              disabled={filtered.length === 0}
              title="Select all shown questions"
            >
              Select all
            </button>
            <button
              type="button"
              className="pill-btn"
              onClick={() => onBulk(filtered, false)}
              disabled={filtered.length === 0}
              title="Unselect all shown questions"
            >
              Unselect all
            </button>
          </>
        )}
      </div>

      <div style={{ maxHeight: 340, overflowY: "auto", paddingRight: 4 }}>
        {groups.length === 0 && (
          <p style={{ color: "var(--text-3)", fontSize: 13 }}>No questions match.</p>
        )}
        {groups.map(([skill, list]) => (
          <div key={skill} style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".04em",
                color: "#7627bb",
                marginBottom: 6,
              }}
            >
              {skill} ({list.length})
            </div>
            {list.map((it) => {
              const checked = selectedIds.has(it.id);
              return (
                <label
                  key={it.id}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    padding: "6px 0",
                    cursor: "pointer",
                    borderTop: "1px solid var(--border-1)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(it)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: 13, flex: 1 }}>
                    {it.text}
                    <span style={{ marginLeft: 6 }}>
                      {(it.levels ?? []).map((l) => (
                        <span
                          key={l}
                          style={{
                            fontSize: 10,
                            color: "var(--text-4)",
                            border: "1px solid var(--border-1)",
                            borderRadius: 4,
                            padding: "0 4px",
                            marginRight: 3,
                          }}
                        >
                          {l}
                        </span>
                      ))}
                      {recommendedIds.has(it.id) && (
                        <span style={{ fontSize: 10, color: "#34a853", fontWeight: 700 }}>
                          ★ recommended
                        </span>
                      )}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
